import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("profiles")
      .select("id, email, display_name, full_name, plan, created_at, onboarded_at, banned")
      .eq("id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const { count: signalsCount } = await supabaseAdmin
      .from("signals")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId);
    return { profile: data, signalsCount: signalsCount ?? 0 };
  });

export const completeOnboarding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("profiles")
      .update({ onboarded_at: new Date().toISOString() })
      .eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const completeOAuthOnboarding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        plan: z.enum(["free", "pro", "elite"]),
        terms_agreed: z.literal(true),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();
    const { error } = await supabaseAdmin
      .from("profiles")
      .update({
        plan: data.plan,
        terms_agreed_at: now,
        onboarded_at: now,
      })
      .eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true, plan: data.plan };
  });

export const getOnboardingStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("profiles")
      .select(
        "onboarded_at, terms_agreed_at, plan, created_at, suitability_confirmed_at, risk_disclosure_version, disclaimer_version, terms_version",
      )
      .eq("id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);

    // Read current compliance versions from site_settings
    const {
      CURRENT_DISCLAIMER_VERSION,
      CURRENT_RISK_DISCLOSURE_VERSION,
      CURRENT_TERMS_VERSION,
      DEFAULT_VERSIONS,
    } = await import("@/lib/compliance/constants");
    const { data: vset } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", "compliance_versions")
      .maybeSingle();
    const versions =
      (vset?.value as
        | { risk_disclosure?: string; disclaimer?: string; terms?: string }
        | undefined) ?? DEFAULT_VERSIONS;
    const requiredRisk = versions.risk_disclosure ?? CURRENT_RISK_DISCLOSURE_VERSION;
    const requiredDisc = versions.disclaimer ?? CURRENT_DISCLAIMER_VERSION;
    const requiredTerms = versions.terms ?? CURRENT_TERMS_VERSION;

    // Soft gate: only require suitability for accounts created on/after the rollout date.
    const SUITABILITY_REQUIRED_FROM = new Date("2026-06-05T00:00:00Z").getTime();
    const createdMs = data?.created_at ? new Date(data.created_at).getTime() : 0;
    const newUser = createdMs >= SUITABILITY_REQUIRED_FROM;
    const suitabilityMissing = newUser && !data?.suitability_confirmed_at;

    // Version drift: any disclaimer/risk/terms version bump forces re-acceptance.
    const versionStale =
      !!data?.suitability_confirmed_at &&
      ((data.risk_disclosure_version ?? "") !== requiredRisk ||
        (data.disclaimer_version ?? "") !== requiredDisc ||
        ((data as { terms_version?: string | null }).terms_version ?? "") !== requiredTerms);

    const needsOnboarding =
      !data?.onboarded_at ||
      !data?.terms_agreed_at ||
      suitabilityMissing ||
      versionStale;

    return {
      needsOnboarding,
      plan: data?.plan ?? "free",
      versionStale,
    };
  });


export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        full_name: z.string().trim().min(1).max(120).optional(),
        plan: z.enum(["free", "pro", "elite"]).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const patch: { full_name?: string; display_name?: string; plan?: "free" | "pro" | "elite" } = {};
    if (data.full_name !== undefined) {
      patch.full_name = data.full_name;
      patch.display_name = data.full_name;
    }
    if (data.plan !== undefined) patch.plan = data.plan;
    const { error } = await supabaseAdmin
      .from("profiles")
      .update(patch)
      .eq("id", context.userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listMySignals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("signals")
      .select("*")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { signals: data ?? [] };
  });
